import { Link } from 'react-router-dom'
import { Phone, CheckCircle, Clock, TrendingUp, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCalls } from '../hooks/useCalls'
import { formatDuration } from '@/shared/constants/calls'

/**
 * CallsMetrics Component
 *
 * Displays call analytics summary on the dashboard
 * - Total calls (last 7 days)
 * - Success rate
 * - Average duration
 * - Completed calls
 */
export function CallsMetrics() {
  // Fetch recent calls for analytics
  const { data, isLoading, error } = useCalls({ limit: 100 })

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Voice Agent Calls</CardTitle>
          <Phone className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-20" />
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Voice Agent Calls</CardTitle>
          <AlertCircle className="h-4 w-4 text-destructive" />
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load call metrics</p>
        </CardContent>
      </Card>
    )
  }

  const calls = data?.calls || []
  const totalCalls = data?.pagination.total || 0

  // Calculate metrics
  const completedCalls = calls.filter((c) => c.status === 'completed').length
  const successfulCalls = calls.filter((c) => c.callSuccessful === true).length
  const successRate = completedCalls > 0 ? Math.round((successfulCalls / completedCalls) * 100) : 0

  const totalDuration = calls.reduce((sum, c) => sum + (c.durationSeconds || 0), 0)
  const avgDuration = calls.length > 0 ? Math.round(totalDuration / calls.length) : 0

  // Get calls from last 24 hours
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
  const recentCalls = calls.filter((c) => new Date(c.createdAt) > oneDayAgo).length

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Voice Agent Calls</CardTitle>
        <Phone className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Main stat */}
          <div>
            <div className="text-2xl font-bold">{totalCalls}</div>
            <p className="text-xs text-muted-foreground">Total calls recorded</p>
          </div>

          {/* Secondary stats grid */}
          <div className="grid grid-cols-3 gap-4 pt-2 border-t">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-500" />
                <span className="text-lg font-semibold">{successRate}%</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Success rate</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Clock className="h-3 w-3 text-blue-500" />
                <span className="text-lg font-semibold">{formatDuration(avgDuration)}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Avg duration</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <TrendingUp className="h-3 w-3 text-orange-500" />
                <span className="text-lg font-semibold">{recentCalls}</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Last 24h</p>
            </div>
          </div>

          {/* Link to calls page */}
          <Button variant="outline" size="sm" className="w-full" asChild>
            <Link to="/dashboard/calls">View all calls</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
